import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { Link2, Unlink, CheckCircle, ArrowUpRight, ArrowDownLeft, Filter, RefreshCw, AlertCircle, FileText, Receipt, Wallet, Building2, TrendingUp, Home, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Match types
const MATCH_TYPES = {
  EXPENSE: { label: 'Spesa', icon: Receipt, color: 'bg-red-100 text-red-800' },
  PAYMENT: { label: 'Pagamento', icon: Wallet, color: 'bg-blue-100 text-blue-800' },
  INCOME: { label: 'Entrata', icon: TrendingUp, color: 'bg-green-100 text-green-800' },
  QUOTA: { label: 'Quota condomino', icon: Home, color: 'bg-amber-100 text-amber-800' },
  MANUAL: { label: 'Manuale', icon: FileText, color: 'bg-gray-100 text-gray-800' }
};

// Da dove si prende l'elenco, per ogni tipo. Le quote fanno storia a se': hanno
// una rotta propria perche' il movimento puo' spalmarsi su piu' di una.
const ELENCHI = {
  EXPENSE: { rotta: 'expenses', titolo: 'Scegli la spesa' },
  PAYMENT: { rotta: 'payments', titolo: 'Scegli il pagamento' },
  INCOME: { rotta: 'cash-ledger', titolo: 'Scegli l\'entrata di cassa' }
};

const euro = (n) => (Number(n) || 0).toLocaleString('it-IT',
  { style: 'currency', currency: 'EUR' });

const MESI = ['', 'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];

export const Reconciliation = () => {
  const { token, selectedCondominium } = useAuth();
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const condoId = selectedCondominium?.condominium?.id;
  
  // State
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [showMatched, setShowMatched] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Filtro per anno (gli estratti conto si caricano anno per anno, dal 2014), per tipo di movimento e per testo
  const [anno, setAnno] = useState('');
  const [tipoMov, setTipoMov] = useState('');
  const [cerca, setCerca] = useState('');
  const [quadro, setQuadro] = useState(null);
  const movimenti = useMemo(() => {
    const t = cerca.trim().toLowerCase();
    return t ? transactions.filter((x) => `${x.description || ''} ${x.reference || ''}`.toLowerCase().includes(t)) : transactions;
  }, [transactions, cerca]);
  const annoCorrente = new Date().getFullYear();
  const anni = useMemo(() => Array.from({ length: annoCorrente - 2013 }, (_, i) => annoCorrente - i), [annoCorrente]);
  const scegliAnno = (a) => {
    const nuovo = String(anno) === String(a) ? '' : String(a);
    setAnno(nuovo);
    setDateFrom(nuovo ? `${nuovo}-01-01` : '');
    setDateTo(nuovo ? `${nuovo}-12-31` : '');
  };
  
  // Dialog states
  const [isMatchDialogOpen, setIsMatchDialogOpen] = useState(false);
  // Si apre senza tipo scelto. 'MANUAL' era il valore di partenza ed e' l'unico
  // tipo che non chiede altro: un clic distratto lo confermava senza opporsi,
  // ed e' gia' costato un aggancio sbagliato. Ora il tasto resta spento finche'
  // il tipo non lo sceglie una persona.
  const [matchAction, setMatchAction] = useState({
    match_type: '',
    matched_id: '',
    notes: '',
    create_payment: false
  });

  // Selettore: al posto dell'ID scritto a mano
  const [entita, setEntita] = useState([]);          // spese / pagamenti / entrate
  const [caricandoElenco, setCaricandoElenco] = useState(false);
  const [posizioni, setPosizioni] = useState([]);    // condomini con quote scoperte
  const [cercaQuota, setCercaQuota] = useState('');
  // Il carrello: PIU' condomini nello stesso movimento, non uno solo.
  //
  // Serviva per gli accrediti POS del portiere, che la banca versa sommando quello che
  // hanno pagato piu' persone in giorni diversi. Con un condomino alla volta quei
  // movimenti non avevano modo di essere spezzati e restavano li' -- 282 accrediti per
  // 126.367,78 al 19/09/2026.
  //
  // Il server non ha mai avuto un vincolo sulla persona: `valida_allocazioni` guarda
  // quota, voce, residuo e la somma, e la rotta carica le quote con un `$in` sugli id
  // senza filtrare per soggetto. Verificato a secco, non dedotto. Quindi qui non si
  // aggira niente: si smette solo di stringere piu' del necessario.
  const [carrello, setCarrello] = useState([]);      // [posizione, ...]
  const [caricandoPosizione, setCaricandoPosizione] = useState(null);
  // chiave "<quota_id>|<voce>": una quota puo' avere l'ordinaria agganciata e l'acqua no
  const [allocazioni, setAllocazioni] = useState({});

  // Fetch functions
  const fetchAccounts = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/bank-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAccounts(data);
        if (data.length > 0 && !selectedAccount) {
          setSelectedAccount(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
    setLoading(false);
  }, [condoId, token, selectedAccount]);

  const fetchTransactions = useCallback(async () => {
    if (!condoId || !selectedAccount) return;
    
    let url = `${API_URL}/api/condominiums/${condoId}/bank-transactions?bank_account_id=${selectedAccount.id}`;
    if (!showMatched) url += '&is_matched=false';
    if (dateFrom) url += `&from_date=${dateFrom}`;
    if (dateTo) url += `&to_date=${dateTo}`;
    if (tipoMov) url += `&transaction_type=${tipoMov}`;
    url += '&limit=2000';       // un anno intero di estratto conto; il tetto predefinito (500) tagliava gli anni piu' vecchi
    
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setTransactions(await res.json());
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  }, [condoId, token, selectedAccount, showMatched, dateFrom, dateTo, tipoMov]);

  // Quadro del periodo scelto: tutti i movimenti (riconciliati e non), indipendente dal pulsante «solo da riconciliare»
  const fetchQuadro = useCallback(async () => {
    if (!condoId || !selectedAccount) return;
    let url = `${API_URL}/api/condominiums/${condoId}/bank-transactions?bank_account_id=${selectedAccount.id}&limit=2000`;
    if (dateFrom) url += `&from_date=${dateFrom}`;
    if (dateTo) url += `&to_date=${dateTo}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const righe = await res.json();
      const totale = Number(res.headers.get('X-Total-Count') || righe.length);
      const entrate = righe.filter((t) => Number(t.amount) > 0).reduce((x, t) => x + Number(t.amount), 0);
      const uscite = righe.filter((t) => Number(t.amount) < 0).reduce((x, t) => x + Number(t.amount), 0);
      setQuadro({ totale, mostrati: righe.length, entrate, uscite, riconciliati: righe.filter((t) => t.is_matched).length });
    } catch (error) {
      console.error('Error fetching quadro:', error);
    }
  }, [condoId, token, selectedAccount, dateFrom, dateTo]);
  useEffect(() => { fetchQuadro(); }, [fetchQuadro]);

  const fetchSummary = useCallback(async () => {
    if (!condoId || !selectedAccount) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/bank-accounts/${selectedAccount.id}/reconciliation-summary`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) setSummary(await res.json());
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [condoId, token, selectedAccount]);

  const fetchSuggestions = async (transactionId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/bank-transactions/${transactionId}/suggestions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) setSuggestions(await res.json());
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    if (selectedAccount) {
      fetchTransactions();
      fetchSummary();
    }
  }, [fetchTransactions, fetchSummary, selectedAccount]);

  // Open match dialog
  const openMatchDialog = async (transaction) => {
    setSelectedTransaction(transaction);
    setMatchAction({
      match_type: '',
      matched_id: '',
      notes: '',
      create_payment: false
    });
    setSuggestions([]);
    setEntita([]);
    setPosizioni([]);
    setCarrello([]);
    setAllocazioni({});
    setCercaQuota('');
    setIsMatchDialogOpen(true);
    await fetchSuggestions(transaction.id);
  };

  // Cambiando tipo si riparte da capo: le scelte fatte per un tipo non valgono
  // per un altro, e portarsele dietro e' il modo piu' rapido per scrivere
  // un'allocazione su un elemento sbagliato.
  const cambiaTipo = (tipo) => {
    setMatchAction({ ...matchAction, match_type: tipo, matched_id: '' });
    setCarrello([]);
    setAllocazioni({});
    setEntita([]);
    setPosizioni([]);
    if (tipo === 'QUOTA') caricaQuoteAperte('', selectedTransaction?.amount);
    else if (ELENCHI[tipo]) caricaElenco(tipo);
  };

  // ---------------------------------------------------------- selettore
  // Elenco di spese / pagamenti / entrate fra cui scegliere.
  const caricaElenco = useCallback(async (tipo) => {
    const conf = ELENCHI[tipo];
    if (!conf || !condoId) { setEntita([]); return; }
    setCaricandoElenco(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/${conf.rotta}`,
        { headers: { Authorization: `Bearer ${token}` } });
      const dati = res.ok ? await res.json() : [];
      setEntita(Array.isArray(dati) ? dati : (dati.items || []));
    } catch { setEntita([]); }
    setCaricandoElenco(false);
  }, [condoId, token]);

  // Condomini con quote scoperte, con la ripartizione gia' proposta dal server.
  // La proposta NON si calcola qui: e' una regola contabile, vive nel backend
  // dove ha i suoi test, e riscritta in JS divergerebbe alla prima correzione.
  const caricaQuoteAperte = useCallback(async (testo, importo) => {
    if (!condoId) return;
    setCaricandoElenco(true);
    try {
      const p = new URLSearchParams({ q: testo || '', importo: String(Math.abs(importo || 0)) });
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/riconciliazione/quote-aperte?${p}`,
        { headers: { Authorization: `Bearer ${token}` } });
      const dati = res.ok ? await res.json() : { posizioni: [] };
      setPosizioni(dati.posizioni || []);
    } catch { setPosizioni([]); }
    setCaricandoElenco(false);
  }, [condoId, token]);

  // La chiave di un'allocazione: quota E voce, perche' la stessa quota puo' avere
  // l'ordinaria agganciata e l'acqua no.
  const chiave = (quotaId, voce) => `${quotaId}|${voce}`;

  const idPosizione = (pos) => pos?.person_id || pos?.intestatario;

  // Aggiunge un condomino al carrello SENZA togliere gli altri.
  //
  // La proposta che arriva con l'elenco e' calcolata sull'INTERO movimento: applicarla
  // tale e quale al secondo condomino assegnerebbe due volte lo stesso denaro. Quindi
  // per chi entra dopo si richiede al server una proposta dimensionata su QUELLO CHE
  // RESTA. La ripartizione resta un conto del backend, dove ha i suoi test: rifarla in
  // JS vorrebbe dire due regole che divergono alla prima correzione.
  const aggiungiPosizione = async (pos) => {
    const id = idPosizione(pos);
    if (carrello.some((p) => idPosizione(p) === id)) return;   // gia' dentro

    const resta = Math.round((importoMovimento - assegnato) * 100) / 100;
    if (resta <= 0.005) {
      // Il movimento e' gia' tutto assegnato: lo si aggiunge comunque, ma spento.
      // Togliere qualcosa a qualcun altro e' una decisione, non un effetto collaterale.
      setCarrello((prec) => [...prec, pos]);
      toast.info('Il movimento e’ già tutto assegnato: togliere prima da un altro');
      return;
    }

    setCaricandoPosizione(id);
    let daUsare = pos;
    if (carrello.length > 0) {
      try {
        const p = new URLSearchParams({
          q: pos.intestatario || '', importo: String(resta)
        });
        const res = await fetch(
          `${API_URL}/api/condominiums/${condoId}/riconciliazione/quote-aperte?${p}`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const dati = await res.json();
          const trovata = (dati.posizioni || []).find((x) => idPosizione(x) === id);
          if (trovata) daUsare = trovata;
        }
      } catch {
        // Se la richiesta non riesce si aggiunge il condomino senza proposta: meglio
        // nessun importo precompilato che uno calcolato sul movimento intero.
        daUsare = { ...pos, proposta: [] };
      }
    }
    setCaricandoPosizione(null);

    setCarrello((prec) => [...prec, daUsare]);
    setAllocazioni((prec) => {
      const nuovo = { ...prec };
      (daUsare.proposta || []).forEach((a) => {
        nuovo[chiave(a.quota_id, a.voce)] = a.importo;
      });
      return nuovo;
    });
  };

  // Toglie un condomino e con lui TUTTE le sue allocazioni: lasciarle orfane
  // significherebbe mandare al server importi su quote che non sono piu' a schermo.
  const rimuoviPosizione = (pos) => {
    const suoi = new Set();
    (pos.quote || []).forEach((q) => {
      (q.voci || []).forEach((v) => suoi.add(chiave(q.id, v.voce)));
    });
    setAllocazioni((prec) => {
      const nuovo = {};
      Object.entries(prec).forEach(([k, v]) => { if (!suoi.has(k)) nuovo[k] = v; });
      return nuovo;
    });
    setCarrello((prec) => prec.filter((p) => idPosizione(p) !== idPosizione(pos)));
  };

  const cambiaAllocazione = (quotaId, voce, valore) => {
    const n = parseFloat(String(valore).replace(',', '.'));
    const k = chiave(quotaId, voce);
    setAllocazioni((prec) => {
      const nuovo = { ...prec };
      if (valore === '' || valore === null || Number.isNaN(n) || n <= 0) delete nuovo[k];
      else nuovo[k] = Math.round(n * 100) / 100;
      return nuovo;
    });
  };

  // Le scorciatoie. Sono scorciatoie e non obblighi: dopo averle usate ogni importo
  // resta correggibile a mano, e il tasto resta spento finche' non si chiude a zero.
  // Ogni scorciatoia agisce su UN condomino del carrello e lascia in pace gli altri.
  // Prima operavano sull'unico condomino selezionato e potevano sostituire tutto: con
  // piu' persone nello stesso movimento, "Azzera" che svuota anche le righe di un altro
  // e' il modo piu' rapido per disfare un lavoro appena fatto.
  const applicaScorciatoia = (quale, pos) => {
    if (!pos) return;
    const suoi = new Set();
    (pos.quote || []).forEach((q) => {
      (q.voci || []).forEach((v) => suoi.add(chiave(q.id, v.voce)));
    });

    setAllocazioni((prec) => {
      const nuovo = {};
      Object.entries(prec).forEach(([k, v]) => { if (!suoi.has(k)) nuovo[k] = v; });
      if (quale === 'azzera') return nuovo;
      if (quale === 'proposta') {
        (pos.proposta || []).forEach((a) => {
          nuovo[chiave(a.quota_id, a.voce)] = a.importo;
        });
        return nuovo;
      }
      (pos.quote || []).forEach((q) => {
        (q.voci || []).forEach((v) => {
          const acqua = /acqua/i.test(v.voce || '');
          if ((quale === 'acqua' && acqua) || (quale === 'ordinaria' && !acqua)
              || quale === 'tutto') {
            nuovo[chiave(q.id, v.voce)] = v.residuo;
          }
        });
      });
      return nuovo;
    });
  };

  const assegnato = Object.values(allocazioni).reduce((s, v) => s + (Number(v) || 0), 0);
  const importoMovimento = Math.abs(selectedTransaction?.amount || 0);
  const daAssegnare = Math.round((importoMovimento - assegnato) * 100) / 100;
  const quadra = Math.abs(daAssegnare) < 0.005;

  // Apply suggestion
  const applySuggestion = (suggestion) => {
    const matchType = suggestion.entity_type.toUpperCase();
    setMatchAction({
      ...matchAction,
      match_type: matchType,
      matched_id: suggestion.entity_id,
      notes: `Auto-match: ${suggestion.match_reason}`
    });
  };

  // Reconcile transaction
  const handleReconcile = async () => {
    if (!selectedTransaction) return;
    // Secondo cancello, come per le allocazioni: il tasto e' gia' spento senza
    // un tipo, ma la grafica si puo' aggirare.
    if (!matchAction.match_type) {
      toast.error('Scegliere il tipo di riconciliazione');
      return;
    }

    const corpo = { ...matchAction };
    if (matchAction.match_type === 'QUOTA') {
      corpo.allocazioni = Object.entries(allocazioni).map(([k, importo]) => {
        const taglio = k.lastIndexOf('|');   // la voce puo' contenere spazi, non '|'
        return { quota_id: k.slice(0, taglio), voce: k.slice(taglio + 1), importo };
      });
      // Il tasto e' gia' spento quando non quadra: questo e' il secondo cancello,
      // perche' il primo sta nella grafica e la grafica si puo' aggirare.
      // Il terzo, quello che conta, e' nel backend.
      if (corpo.allocazioni.length === 0) {
        toast.error('Scegliere almeno una quota');
        return;
      }
    }

    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/bank-transactions/${selectedTransaction.id}/reconcile`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(corpo)
        }
      );
      
      if (res.ok) {
        toast.success('Movimento riconciliato');
        setIsMatchDialogOpen(false);
        fetchTransactions();
        fetchSummary();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore riconciliazione');
      }
    } catch (error) {
      toast.error('Errore riconciliazione');
    }
  };

  // Unmatch transaction
  const handleUnmatch = async (transactionId) => {
    if (!window.confirm('Annullare la riconciliazione di questo movimento?')) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/bank-transactions/${transactionId}/unmatch`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        toast.success('Riconciliazione annullata');
        fetchTransactions();
        fetchSummary();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Helpers
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const canEdit = userRole === 'ADMIN' || userRole === 'ACCOUNTANT';

  // Access check
  if (userRole === 'RESIDENT') {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
              <h2 className="text-xl font-semibold mb-2">Accesso Negato</h2>
              <p className="text-muted-foreground">
                Solo amministratori e contabili possono accedere alla riconciliazione.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Riconciliazione"
        subtitle="Abbinamento movimenti bancari"
        actions={accounts.length > 0 ? (
          <Select
            value={selectedAccount?.id || ''}
            onValueChange={(v) => setSelectedAccount(accounts.find(a => a.id === v))}
          >
            <SelectTrigger className="w-[300px] border-white/40 text-white hover:bg-white/10">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Seleziona conto" />
            </SelectTrigger>
            <SelectContent>
              {accounts.map((account) => (
                <SelectItem key={account.id} value={account.id}>
                  {account.bank_name} - {formatCurrency(account.current_balance)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
      />

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
      ) : accounts.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessun conto bancario configurato</p>
              <p className="text-sm mt-2">Vai a Import Estratti per configurare un conto</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-sm text-muted-foreground">Movimenti Totali</div>
                  <div className="text-2xl font-bold">{summary.total_transactions}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-sm text-muted-foreground">Da Riconciliare</div>
                  <div className="text-2xl font-bold text-orange-600">{summary.unmatched_transactions}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-sm text-muted-foreground">Entrate</div>
                  <div className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_credits)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-sm text-muted-foreground">Uscite</div>
                  <div className="text-2xl font-bold text-red-600">{formatCurrency(summary.total_debits)}</div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Quadro del periodo */}
          {quadro && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  {anno ? `Estratto conto ${anno}` : (dateFrom || dateTo) ? 'Periodo scelto' : 'Tutti gli anni'}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                <div><div className="text-muted-foreground">Movimenti</div><div className="text-xl font-bold">{quadro.totale}</div></div>
                <div><div className="text-muted-foreground">Entrate</div><div className="text-xl font-bold text-green-600">{formatCurrency(quadro.entrate)}</div></div>
                <div><div className="text-muted-foreground">Uscite</div><div className="text-xl font-bold text-red-600">{formatCurrency(quadro.uscite)}</div></div>
                <div><div className="text-muted-foreground">Saldo del periodo</div><div className="text-xl font-bold">{formatCurrency(quadro.entrate + quadro.uscite)}</div></div>
                <div><div className="text-muted-foreground">Riconciliati</div><div className="text-xl font-bold">{quadro.riconciliati} / {quadro.mostrati}</div></div>
                {quadro.totale > quadro.mostrati && <div className="col-span-full text-xs text-orange-700">Il periodo ha {quadro.totale} movimenti: ne sono mostrati {quadro.mostrati}. Restringi le date.</div>}
              </CardContent>
            </Card>
          )}

          {/* Filters */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center gap-2 pb-3 border-b mb-3">
                <span className="text-sm font-medium mr-1">Anno:</span>
                <Button size="sm" variant={anno === '' ? 'default' : 'outline'} onClick={() => scegliAnno('')}>Tutti</Button>
                {anni.map((a) => (
                  <Button key={a} size="sm" variant={String(anno) === String(a) ? 'default' : 'outline'} onClick={() => scegliAnno(a)}>{a}</Button>
                ))}
              </div>
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  <span className="font-medium">Filtri</span>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Da:</Label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="w-36 h-8"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">A:</Label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="w-36 h-8"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Tipo:</Label>
                    <select className="h-8 rounded-md border bg-background px-2 text-sm" value={tipoMov} onChange={(e) => setTipoMov(e.target.value)}>
                      <option value="">Entrate e uscite</option>
                      <option value="CREDIT">Solo entrate</option>
                      <option value="DEBIT">Solo uscite</option>
                    </select>
                  </div>
                  <Input placeholder="Cerca nella descrizione" value={cerca} onChange={(e) => setCerca(e.target.value)} className="w-56 h-8" />
                  <Button
                    variant={showMatched ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowMatched(!showMatched)}
                  >
                    {showMatched ? 'Tutti' : 'Solo da riconciliare'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={fetchTransactions}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Transactions Table */}
          <Card>
            <CardHeader>
              <CardTitle>Movimenti Bancari</CardTitle>
            </CardHeader>
            <CardContent>
              {movimenti.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p>Tutti i movimenti sono stati riconciliati!</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {movimenti.map((trans) => {
                      const isCredit = trans.amount >= 0;
                      const matchInfo = trans.match_type ? MATCH_TYPES[trans.match_type] : null;
                      
                      return (
                        <TableRow key={trans.id} className={trans.is_matched ? 'bg-green-50' : ''}>
                          <TableCell className="whitespace-nowrap">
                            {formatDate(trans.transaction_date)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {isCredit ? (
                                <ArrowDownLeft className="h-4 w-4 text-green-600 flex-shrink-0" />
                              ) : (
                                <ArrowUpRight className="h-4 w-4 text-red-600 flex-shrink-0" />
                              )}
                              <span className="truncate max-w-[300px]">{trans.description}</span>
                            </div>
                            {trans.reference && (
                              <div className="text-xs text-muted-foreground mt-1">
                                Rif: {trans.reference}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className={`text-right font-mono font-medium ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(trans.amount)}
                          </TableCell>
                          <TableCell>
                            {trans.is_matched ? (
                              <Badge className={matchInfo?.color || 'bg-green-100 text-green-800'}>
                                {matchInfo?.label || 'Riconciliato'}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-orange-600 border-orange-300">
                                Da riconciliare
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {trans.is_matched ? (
                              userRole === 'ADMIN' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleUnmatch(trans.id)}
                                >
                                  <Unlink className="h-4 w-4 mr-1" />
                                  Scollega
                                </Button>
                              )
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openMatchDialog(trans)}
                              >
                                <Link2 className="h-4 w-4 mr-1" />
                                Riconcilia
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Match Dialog */}
          <Dialog open={isMatchDialogOpen} onOpenChange={setIsMatchDialogOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Riconcilia Movimento</DialogTitle>
                <DialogDescription>
                  Abbina questo movimento bancario a una spesa o pagamento
                </DialogDescription>
              </DialogHeader>

              {selectedTransaction && (
                <div className="space-y-4">
                  {/* Transaction Info */}
                  <Card className="bg-slate-50">
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{selectedTransaction.description}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(selectedTransaction.transaction_date)}
                          </p>
                        </div>
                        <span className={`text-xl font-bold ${selectedTransaction.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(selectedTransaction.amount)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Suggestions */}
                  {suggestions.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Suggerimenti</Label>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {suggestions.map((sugg, i) => {
                          const SuggIcon = MATCH_TYPES[sugg.entity_type.toUpperCase()]?.icon || FileText;
                          return (
                            <div
                              key={i}
                              className={`p-3 border rounded-lg cursor-pointer transition-all ${
                                matchAction.matched_id === sugg.entity_id
                                  ? 'border-slate-900 bg-slate-50'
                                  : 'hover:border-slate-400'
                              }`}
                              onClick={() => applySuggestion(sugg)}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-2">
                                  <SuggIcon className="h-4 w-4 mt-1 text-slate-600" />
                                  <div>
                                    <p className="font-medium text-sm">{sugg.entity_description || '-'}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {formatDate(sugg.entity_date)} • {formatCurrency(sugg.entity_amount)}
                                    </p>
                                    <p className="text-xs text-blue-600">{sugg.match_reason}</p>
                                  </div>
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  {sugg.confidence.toFixed(0)}%
                                </Badge>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Manual Match */}
                  <div className="space-y-3">
                    <Label>Tipo Riconciliazione</Label>
                    <Select value={matchAction.match_type} onValueChange={cambiaTipo}>
                      <SelectTrigger>
                        <SelectValue placeholder="Scegli il tipo di riconciliazione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="QUOTA">Quota di un condomino</SelectItem>
                        <SelectItem value="EXPENSE">Collega a Spesa</SelectItem>
                        <SelectItem value="PAYMENT">Collega a Pagamento</SelectItem>
                        <SelectItem value="INCOME">Collega a Entrata</SelectItem>
                        <SelectItem value="MANUAL">Riconciliazione Manuale</SelectItem>
                      </SelectContent>
                    </Select>

                    {/* --- QUOTE: scegli il condomino, poi i mesi da coprire --- */}
                    {matchAction.match_type === 'QUOTA' && (
                      <div className="space-y-3">
                        {/* La ricerca resta SEMPRE a disposizione, anche a carrello
                            pieno: e' cosi' che si aggiunge la seconda persona di un
                            accredito cumulativo. */}
                        <div className="relative">
                          <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                          <Input
                            className="pl-9"
                            value={cercaQuota}
                            onChange={(e) => {
                              setCercaQuota(e.target.value);
                              caricaQuoteAperte(e.target.value, selectedTransaction.amount);
                            }}
                            placeholder={carrello.length === 0
                              ? "Cerca il condomino o l'unità (es. Torina, A17). Compaiono tutte le sue unità insieme"
                              : 'Cerca un altro condomino da aggiungere a questo stesso movimento'}
                          />
                        </div>

                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {caricandoElenco && (
                            <p className="text-sm text-muted-foreground p-2">Carico…</p>
                          )}
                          {!caricandoElenco && posizioni.length === 0 && (
                            <p className="text-sm text-muted-foreground p-2">
                              Nessuna quota scoperta con questo nome.
                            </p>
                          )}
                          {posizioni.map((pos) => {
                            const id = idPosizione(pos);
                            const dentro = carrello.some((p) => idPosizione(p) === id);
                            return (
                              <div
                                key={id}
                                className={`p-3 border rounded-lg flex justify-between items-center ${
                                  dentro
                                    ? 'bg-slate-50 opacity-60'
                                    : 'cursor-pointer hover:border-slate-400'}`}
                                onClick={() => { if (!dentro) aggiungiPosizione(pos); }}
                              >
                                <div>
                                  <p className="font-medium text-sm">{pos.intestatario || '—'}</p>
                                  <p className="text-xs text-muted-foreground">
                                    unità {(pos.unita || []).join(', ')} • {pos.quote.length} quote scoperte
                                  </p>
                                </div>
                                <span className="text-sm font-semibold">
                                  {caricandoPosizione === id
                                    ? '…'
                                    : (dentro ? 'già nel movimento' : euro(pos.residuo))}
                                </span>
                              </div>
                            );
                          })}
                        </div>

                        {carrello.map((pos) => {
                          // Quanto e' stato imputato a QUESTA persona. Con piu' condomini
                          // sullo stesso movimento il totale generale non basta piu':
                          // senza il parziale per persona non si sa a chi si sta dando
                          // che cosa, ed e' proprio l'errore che il carrello puo'
                          // introdurre.
                          let suo = 0;
                          (pos.quote || []).forEach((q) => {
                            (q.voci || []).forEach((v) => {
                              suo += Number(allocazioni[chiave(q.id, v.voce)] || 0);
                            });
                          });
                          return (
                            <div key={idPosizione(pos)} className="border rounded-lg p-3 space-y-2">
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="font-medium text-sm">{pos.intestatario}</p>
                                  <p className="text-xs text-muted-foreground">
                                    unità {(pos.unita || []).join(', ')} •
                                    {' '}scoperto {euro(pos.residuo)}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-semibold">{euro(suo)}</p>
                                  <Button variant="ghost" size="sm"
                                    onClick={() => rimuoviPosizione(pos)}>
                                    Togli
                                  </Button>
                                </div>
                              </div>

                              {/* Scorciatoie: propongono, non impongono. Agiscono solo
                                  su questo condomino. */}
                              <div className="flex flex-wrap gap-2">
                                <Button variant="outline" size="sm"
                                  onClick={() => applicaScorciatoia('proposta', pos)}>
                                  Copri dal più vecchio
                                </Button>
                                <Button variant="outline" size="sm"
                                  onClick={() => applicaScorciatoia('tutto', pos)}>
                                  Tutto l'arretrato
                                </Button>
                                <Button variant="outline" size="sm"
                                  onClick={() => applicaScorciatoia('ordinaria', pos)}>
                                  Solo ordinaria
                                </Button>
                                <Button variant="outline" size="sm"
                                  onClick={() => applicaScorciatoia('acqua', pos)}>
                                  Solo acqua
                                </Button>
                                <Button variant="ghost" size="sm"
                                  onClick={() => applicaScorciatoia('azzera', pos)}>
                                  Azzera
                                </Button>
                              </div>

                              <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
                                {Object.entries(
                                  (pos.quote || []).reduce((acc, q) => {
                                    (acc[q.anno] = acc[q.anno] || []).push(q); return acc;
                                  }, {})
                                ).map(([anno, quote]) => (
                                  <div key={anno}>
                                    <div className="px-3 py-1 bg-slate-100 text-xs font-semibold">
                                      {anno}
                                    </div>
                                    {quote.map((q) => (
                                      <div key={q.id} className="px-3 py-2">
                                        <p className="text-sm font-medium">
                                          {q.unita} · {MESI[q.mese] || q.periodo}
                                        </p>
                                        {(q.voci || []).map((v) => {
                                          const scelta = allocazioni[chiave(q.id, v.voce)];
                                          return (
                                            <div key={v.voce}
                                              className={`mt-1 pl-1 flex items-center gap-3 rounded ${scelta ? 'bg-amber-50' : ''}`}>
                                              <input
                                                type="checkbox"
                                                className="h-4 w-4"
                                                checked={!!scelta}
                                                onChange={(e) => cambiaAllocazione(
                                                  q.id, v.voce, e.target.checked ? v.residuo : '')}
                                              />
                                              <div className="flex-1">
                                                <p className="text-sm">{v.voce}</p>
                                                <p className="text-xs text-muted-foreground">
                                                  residuo {euro(v.residuo)}
                                                  {v.incassato > 0 && ` • già incassati ${euro(v.incassato)}`}
                                                </p>
                                              </div>
                                              <Input
                                                className="w-28 text-right"
                                                value={scelta ?? ''}
                                                onChange={(e) => cambiaAllocazione(
                                                  q.id, v.voce, e.target.value)}
                                                placeholder="0,00"
                                              />
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}

                        {/* Il contatore che tiene onesta l'operazione. Ora somma su
                            TUTTI i condomini del carrello, ed e' l'unica cosa che
                            decide se il tasto si accende. */}
                        {carrello.length > 0 && (
                          <div className={`p-3 rounded-lg text-sm flex justify-between items-center ${
                            quadra ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-900'}`}>
                            <span>
                              {quadra
                                ? `Tutto assegnato${carrello.length > 1 ? ` su ${carrello.length} condomini` : ''}`
                                : (daAssegnare > 0
                                  ? 'Restano da assegnare'
                                  : 'Assegnato più del movimento, in eccesso')}
                            </span>
                            <span className="font-bold">{euro(Math.abs(daAssegnare))}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* --- SPESE / PAGAMENTI / ENTRATE: un elenco, non un ID --- */}
                    {ELENCHI[matchAction.match_type] && (
                      <div className="space-y-2">
                        <Label className="text-sm">{ELENCHI[matchAction.match_type].titolo}</Label>
                        {caricandoElenco && (
                          <p className="text-sm text-muted-foreground">Carico…</p>
                        )}
                        {!caricandoElenco && entita.length === 0 && (
                          <p className="text-sm text-muted-foreground">Nessun elemento disponibile.</p>
                        )}
                        {entita.length > 0 && (
                          <Select
                            value={matchAction.matched_id}
                            onValueChange={(v) => setMatchAction({ ...matchAction, matched_id: v })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Scegli dall'elenco" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                              {entita.map((e) => (
                                <SelectItem key={e.id} value={e.id}>
                                  {formatDate(e.invoice_date || e.date || e.due_date)}
                                  {' • '}{euro(e.net_to_pay ?? e.total ?? e.amount)}
                                  {' • '}{e.supplier_name || e.description || '—'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label className="text-sm">Note</Label>
                      <Textarea
                        value={matchAction.notes}
                        onChange={(e) => setMatchAction({ ...matchAction, notes: e.target.value })}
                        placeholder="Note sulla riconciliazione..."
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsMatchDialogOpen(false)}>
                  Annulla
                </Button>
                <Button
                  onClick={handleReconcile}
                  disabled={
                    !matchAction.match_type ||
                    (matchAction.match_type === 'QUOTA' && !quadra) ||
                    (!!ELENCHI[matchAction.match_type] && !matchAction.matched_id)
                  }
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Riconcilia
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};
